import {
  Delete,
  Edit,
  EventBusy,
  Info,
  Movie,
  PlayCircle,
  Replay,
  Timer,
} from '@mui/icons-material';
import { Alert, useMediaQuery, useTheme } from '@mui/material';
import type { DataGridProProps as DataGridProps, GridColDef } from '@mui/x-data-grid-pro';
import { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDeleteEvent } from '~/api/events/delete';
import { useRerunEventRollups } from '~/api/events/rerun-rollups';
import { Chip } from '~/components/Chip';
import { Tooltip } from '~/components/Tooltip';
import { ActionButton } from '~/components/button';
import { SeparatedDataGrid, hiddenHeader } from '~/components/data-grid';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import { EmptyState } from '~/components/empty-state';
import { ListCheckbox } from '~/components/list-row';
import { mergeGridActionProps, type GridActionCellProps } from '~/components/table';
import { useConfirmDialog } from '~/hooks/dialogs';
import CreateEventTypeIllustration from '~/images/illustrations/create-event-type.svg';
import { formatDate } from '~/lib/datetime';
import type {
  NetworkEventTypeEvents__Event as Event,
  NetworkEventTypeEvents__EventType as EventType,
} from '../../queries/events.generated';
import { EditEventDialog } from './edit-event-dialog';

const NoRowsOverlay = () => {
  const { t } = useTranslation();
  return (
    <EmptyState
      illustration={CreateEventTypeIllustration}
      description={t('pages.event_types.events_tab.table.empty_state.description')}
      header={t('pages.event_types.events_tab.table.empty_state.header')}
    />
  );
};

interface RemoveButtonProps extends GridActionCellProps {
  eventType: EventType;
  event: Event;
}

const RemoveButton = ({ eventType, event, ...cellProps }: RemoveButtonProps) => {
  const { t } = useTranslation();
  const [deleteEvent, { called, reset }] = useDeleteEvent();

  const [confirmDelete, confirmDeleteProps] = useConfirmDialog();

  const onClick = async () => {
    if (!(await confirmDelete())) return;
    await deleteEvent({
      variables: { ids: event.id },
    });
    reset();
  };

  return (
    <>
      <ActionButton
        color="error"
        disabled={called || !event.canDestroy.value}
        Icon={Delete}
        {...mergeGridActionProps({ onClick }, cellProps)}
        title={
          event.canDestroy.value
            ? t('common.dialogs.delete')
            : event.canDestroy.reasons?.fullMessages[0] ?? ''
        }
      />

      <ConfirmDialog
        {...confirmDeleteProps}
        confirm={t('common.dialogs.delete')}
        deleteConfirm
        prompt={
          <Trans
            components={{ b: <b /> }}
            i18nKey="pages.event_types.events_tab.table.delete_confirm_prompt"
            values={{ eventName: event.name, eventTypeName: eventType.name }}
          />
        }
        title={t('pages.event_types.events_tab.table.delete_confirm_title')}
      />
    </>
  );
};

const RerunRollupsButton = ({ event, ...cellProps }: { event: Event } & GridActionCellProps) => {
  const { t } = useTranslation();
  const [rerunRollups, { called, reset }] = useRerunEventRollups();

  const [confirmRerun, confirmRerunProps] = useConfirmDialog();

  const onClick = async () => {
    if (!(await confirmRerun())) return;
    try {
      await rerunRollups({ variables: { eventId: event.id } });
    } catch {
      reset();
    }
  };

  return (
    <>
      <ActionButton
        disabled={called || !event.canRerunRollups.value}
        Icon={Replay}
        {...mergeGridActionProps({ onClick }, cellProps)}
        title={
          !event.canRerunRollups.value
            ? t('pages.event_types.events_tab.table.rebuild_sponsor_data_disabled')
            : t('pages.event_types.events_tab.table.rebuild_sponsor_data')
        }
      />

      <ConfirmDialog
        {...confirmRerunProps}
        confirm={t('pages.event_types.events_tab.table.rebuild_sponsor_data')}
        prompt={
          <>
            <Trans
              i18nKey="pages.event_types.events_tab.table.rebuild_sponsor_data_confirm_prompt"
              values={{ eventName: event.name }}
              components={{ b: <b /> }}
            />
            <Alert
              variant="outlined"
              severity="info"
              style={{ marginTop: 16, fontSize: '0.875rem' }}
            >
              {t('pages.event_types.events_tab.table.rebuild_sponsor_data_warning')}
            </Alert>
          </>
        }
        title={t('pages.event_types.events_tab.table.rebuild_sponsor_data')}
      />
    </>
  );
};

const useColumns = (
  editEvent: (event: Event) => void,
  eventType: EventType,
): GridColDef<Event>[] => {
  const { t } = useTranslation();
  const theme = useTheme();
  const expandedChips = useMediaQuery(theme.breakpoints.up('lg'));

  return useMemo(
    () => [
      {
        field: 'name',
        headerName: t('common.labels.name'),
        flex: 0.5,
        renderCell: ({ row }) => (
          <Tooltip
            title={
              row.fcEventId
                ? t('pages.event_types.events_tab.table.game_id_tooltip', { id: row.fcEventId })
                : ''
            }
          >
            <span>{row.name}</span>
          </Tooltip>
        ),
      },
      {
        field: 'start',
        headerName: t('pages.event_types.events_tab.table.columns.start'),
        flex: 0.35,
        renderCell: ({ row }) => {
          return formatDate(row.start);
        },
      },
      {
        field: 'stop',
        headerName: t('pages.event_types.events_tab.table.columns.stop'),
        flex: 0.35,
        renderCell: ({ row }) => {
          return (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {!row.actualStop && (
                <Tooltip
                  describeChild={false}
                  title={t('pages.event_types.events_tab.table.no_reported_end_tooltip')}
                >
                  <Info
                    sx={{ color: 'secondary.main', marginRight: '5px' }}
                    titleAccess={t('pages.event_types.events_tab.table.no_reported_end_tooltip')}
                  />
                </Tooltip>
              )}
              <span>{formatDate(row.actualStop ?? row.stop)}</span>
            </div>
          );
        },
      },
      {
        field: 'indicators',
        ...hiddenHeader(t('common.labels.status')),
        sortable: false,
        disableColumnMenu: true,
        width: expandedChips ? 330 : 120,
        renderCell: ({ row }) => {
          const now = new Date().toISOString();
          const isPlaying = row.contentStart <= now && (row.actualStop ?? row.contentStop) >= now;
          const hasSponsorOverride = !!(row.sponsorStartTime || row.sponsorStopTime);
          const hasShowOverride = row.overrideCount > 0;
          const isExcludedFromReporting = !row.includeInPlayoutReporting;

          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              {isPlaying && (
                <Chip
                  color={'success'}
                  icon={<PlayCircle />}
                  label={t('pages.event_types.events_tab.table.now_playing')}
                  tooltip={t('pages.event_types.events_tab.table.now_playing_tooltip')}
                  labelBreakpoint="lg"
                />
              )}
              {hasSponsorOverride && (
                <Chip
                  color={'primary'}
                  icon={<Timer />}
                  label={t('pages.event_types.events_tab.table.sponsor_override')}
                  tooltip={t('pages.event_types.events_tab.table.sponsor_override_tooltip')}
                  labelBreakpoint="lg"
                />
              )}
              {hasShowOverride && (
                <Chip
                  color={'tertiary'}
                  icon={<Movie />}
                  label={t('pages.event_types.events_tab.table.show_override', {
                    count: row.overrideCount,
                  })}
                  tooltip={t('pages.event_types.events_tab.table.show_override_tooltip_plural', {
                    count: row.overrideCount,
                  })}
                  labelBreakpoint="lg"
                />
              )}
              {isExcludedFromReporting && (
                <Chip
                  color={'info'}
                  icon={<EventBusy />}
                  label={t('pages.event_types.events_tab.table.excluded_from_reporting')}
                  tooltip={t('pages.event_types.events_tab.table.excluded_from_reporting_tooltip')}
                  labelBreakpoint="lg"
                />
              )}
            </div>
          );
        },
      },
      {
        field: 'actions',
        headerName: t('pages.event_types.events_tab.table.columns.actions'),
        type: 'actions',
        minWidth: 135,
        getActions: ({ row }) => {
          return [
            <RerunRollupsButton key={`rerun-${row.id}`} event={row} />,
            <ActionButton
              key={`edit-${row.id}`}
              title={
                row.canUpdate.value
                  ? t('common.dialogs.update')
                  : row.canUpdate.reasons?.fullMessages[0] ?? ''
              }
              disabled={!row.canUpdate.value}
              Icon={Edit}
              onClick={() => editEvent(row)}
            />,
            <RemoveButton key={0} eventType={eventType} event={row} />,
          ];
        },
      },
    ],
    [editEvent, eventType, expandedChips, t],
  );
};

export interface EventsTableProps extends Omit<DataGridProps<Event>, 'columns'> {
  eventType: EventType;
  loading: boolean;
}

export const EventsTable = ({ eventType, loading, ...props }: EventsTableProps) => {
  const [currentEvent, setCurrentEvent] = useState<Event>();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});

  const theme = useTheme();
  const isSmallAndDown = useMediaQuery(theme.breakpoints.down('sm'));
  const isMediumAndDown = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    setColumnVisibility({
      indicators: !isSmallAndDown,
      start: !isMediumAndDown,
      stop: !isMediumAndDown,
    });
  }, [isSmallAndDown, isMediumAndDown]);

  const editEvent = (event: Event) => {
    setCurrentEvent(event);
    setEditDialogOpen(true);
  };

  const columns = useColumns(editEvent, eventType);

  return (
    <>
      <SeparatedDataGrid
        columns={columns}
        columnVisibilityModel={columnVisibility}
        loading={loading}
        isRowSelectable={({ row }) => row.canDestroy.value}
        slots={{
          baseCheckbox: ListCheckbox,
          columnResizeIcon: () => null,
          noRowsOverlay: NoRowsOverlay,
        }}
        {...props}
      />
      {currentEvent && (
        <EditEventDialog
          close={() => setEditDialogOpen(false)}
          open={editDialogOpen}
          event={currentEvent}
        />
      )}
    </>
  );
};
